import type { PostgrestError } from "@supabase/supabase-js";

/**
 * Traduccion de los codigos que lanzan las funciones de PostgreSQL.
 *
 * Ningun componente muestra el mensaje crudo del motor: filtra nombres de
 * tablas y columnas, y no le dice nada util a quien atiende una tienda.
 */
const MESSAGES: Record<string, string> = {
  OUT_OF_STOCK: "No hay stock suficiente para completar la operación.",
  INVALID_ITEMS: "Alguno de los productos ya no está disponible.",
  EMPTY_CART: "El pedido no tiene productos.",
  CART_TOO_LARGE: "El pedido tiene demasiadas líneas.",
  INVALID_QUANTITY: "La cantidad indicada no es válida.",
  MINIMUM_ORDER: "El pedido no alcanza el mínimo de compra.",
  NO_SHIPPING_RATE: "No hay tarifa de reparto para ese distrito.",
  DISTRICT_REQUIRED: "Falta el distrito de entrega.",
  ADDRESS_REQUIRED: "Falta la dirección de entrega.",
  NO_DEFAULT_WAREHOUSE: "La tienda no tiene un almacén configurado.",
  INVALID_PAYMENT_METHOD: "Ese método de pago no está disponible.",
  INVALID_TRANSITION: "Ese cambio de estado no está permitido.",
  ORDER_NOT_FOUND: "No se encontró el pedido.",
  ALREADY_CANCELLED: "El pedido ya estaba cancelado.",
  CANNOT_CANCEL_COMPLETED:
    "Un pedido entregado no se cancela: se registra una devolución.",
  FORBIDDEN: "Tu rol no permite esta acción.",
  MODULE_DISABLED: "Este módulo no está activo en tu plan.",
  MODULE_NOT_AVAILABLE: "Ese módulo todavía no está disponible.",
  REASON_REQUIRED: "Indica el motivo del ajuste.",
  INVENTORY_DIRECT_WRITE: "El stock solo se modifica desde el ajuste de inventario.",
  INVENTORY_LEDGER_IMMUTABLE: "Los movimientos de inventario no se modifican.",
  LAST_TENANT_OWNER: "La tienda debe conservar al menos un propietario activo.",
  INVALID_TOKEN: "El enlace de seguimiento no es válido.",
  ORG_NOT_FOUND: "No se encontró la tienda.",
  CUSTOMER_NAME_REQUIRED: "Falta el nombre del cliente.",
  PRODUCT_KIND_NOT_ALLOWED: "Tu tienda no tiene habilitado ese tipo en el catálogo.",

  // Subida de imagenes (Edge Functions + R2)
  UNSUPPORTED_FILE_TYPE: "Formato no admitido. Usa JPG, PNG, WebP o AVIF.",
  FILE_TOO_LARGE: "La imagen pesa más de 5 MB.",
  INVALID_FILE_SIZE: "No se pudo leer el tamaño del archivo.",
  PRESIGN_FAILED: "El almacenamiento de imágenes no está disponible.",
  UPLOAD_FAILED: "La imagen no llegó completa. Inténtalo de nuevo.",
  OBJECT_NOT_FOUND: "La imagen no llegó al almacenamiento.",
  STORAGE_KEY_MISMATCH: "Esa imagen no pertenece a tu tienda.",
  IMAGE_INSERT_FAILED: "No se pudo registrar la imagen.",
  IMAGE_NOT_FOUND: "Esa imagen ya no existe.",
  IMAGE_DELETE_FAILED: "No se pudo quitar la imagen.",
  ENTITY_NOT_FOUND: "El producto ya no existe.",
  STORAGE_NOT_CONFIGURED:
    "El almacenamiento de imágenes todavía no está configurado.",
};

/** Códigos de PostgreSQL que conviene traducir aparte de los nuestros. */
const SQLSTATE: Record<string, string> = {
  "23505": "Ya existe un registro con esos datos.",
  "23503": "No se puede completar: hay información relacionada.",
  "23514": "Alguno de los valores no cumple las reglas de la tienda.",
  "42501": "No tienes permiso para ver o modificar esto.",
  PGRST301: "Tu sesión expiró. Vuelve a entrar.",
};

export function friendlyError(error: unknown): string {
  if (!error) return "Ocurrió un error inesperado.";

  const pg = error as Partial<PostgrestError> & { message?: string };
  const raw = pg.message ?? String(error);

  // Nuestras excepciones llegan como `CODIGO:detalle`.
  const tag = raw.match(/\b([A-Z][A-Z_]{3,})\b/)?.[1];
  const known = tag ? MESSAGES[tag] : undefined;
  if (known) return known;

  // «Ya existe un registro con esos datos» no dice cual de los dos campos
  // repetido hay que corregir. El nombre de la restriccion si lo sabe.
  if (pg.code === "23505") {
    if (raw.includes("products_organization_id_slug_key")) {
      return "Ya tienes un producto con ese enlace. Cambia el enlace en la tienda.";
    }
    if (raw.includes("product_variants_organization_id_sku_key")) {
      return "Ese SKU ya está en uso por otra variante.";
    }
  }

  const bySqlState = pg.code ? SQLSTATE[pg.code] : undefined;
  if (bySqlState) return bySqlState;

  if (raw.includes("Failed to fetch") || raw.includes("NetworkError")) {
    return "Sin conexión. Revisa tu red e inténtalo de nuevo.";
  }

  return "No se pudo completar la operación. Inténtalo de nuevo.";
}

/** El identificador que acompaña a un OUT_OF_STOCK, para resaltar la línea. */
export function outOfStockVariantId(error: unknown): string | null {
  const raw = (error as { message?: string })?.message ?? "";
  return raw.match(/OUT_OF_STOCK:([0-9a-f-]{36})/i)?.[1] ?? null;
}
